import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, ArrowRight, Building2, Trash2, HelpCircle, Library, TrendingUp, Home, Wrench, Check, Users, Eye, Pencil, Globe, Ruler } from "lucide-react";
import UpgradeModal from "@/components/UpgradeModal";
import PreProjectNoticeDialog from "@/components/PreProjectNoticeDialog";
import { listProjects, createProject, deleteProject, http } from "@/lib/api";
import { estimatedRenovationCost, estimatedROI, formatCurrency, formatPct } from "@/lib/calculations";
import { PROJECT_GOALS, getGoal } from "@/lib/projectGoals";
import {
  CONSTRUCTION_TYPES, ROOF_TYPES, FOUNDATION_TYPES, WALL_FINISHES, isConstructionComplete,
} from "@/lib/constructionTypes";
import { CURRENCIES } from "@/lib/CurrencyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import UserMenu from "@/components/UserMenu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const GOAL_ICON = { flip: TrendingUp, sell: Home, live: Wrench };

// Region labels paired to each currency — shown in the Step 2 region picker.
const REGION_BY_CURRENCY = {
  USD: "United States",
  CAD: "Canada",
  GBP: "United Kingdom",
  EUR: "Eurozone",
  AUD: "Australia",
  NZD: "New Zealand",
  ZAR: "South Africa",
};

// Space-type choices shown in the SpaceCost create flow. Mirror the
// OverviewTab list so the badge displays sensibly.
const SPACE_TYPES = [
  { value: "house", label: "House" },
  { value: "apartment", label: "Apartment" },
  { value: "townhouse", label: "Townhouse" },
  { value: "duplex", label: "Duplex" },
  { value: "commercial", label: "Commercial / Office" },
  { value: "other", label: "Other" },
];

// SpaceCost is the room-level sister product — its New-Project flow skips
// the FlipCost goal/construction steps.
const VALID_PRODUCTS = new Set(["flipcost", "spacecost"]);

export default function ProjectList() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [goal, setGoal] = useState("flip");
  const [currency, setCurrency] = useState("USD");
  const [construction, setConstruction] = useState({
    construction_type: "",
    roof_type: "",
    foundation_type: "",
    wall_finish: "",
  });
  // Property/space type — collected up-front for SpaceCost so the user
  // doesn't re-enter it on the Overview tab.
  const [propertyType, setPropertyType] = useState("house");
  const [seedLibraryFrom, setSeedLibraryFrom] = useState("");
  const [newName, setNewName] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [upgradeInfo, setUpgradeInfo] = useState(null);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [billing, setBilling] = useState(null); // {plan, project_limit, can_create_project, ...}
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Active product tab — drives both the visible list and the New Project flow.
  const initialProduct = (searchParams.get("product") || "flipcost").toLowerCase();
  const [productTab, setProductTab] = useState(VALID_PRODUCTS.has(initialProduct) ? initialProduct : "flipcost");
  const isSpaceCostTab = productTab === "spacecost";

  // Keep the URL in sync with the active tab so deep-links / refreshes preserve it.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (productTab === "flipcost") next.delete("product");
    else next.set("product", productTab);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productTab]);

  const load = async () => {
    try {
      setLoading(true);
      const data = await listProjects();
      setProjects(data);
    } catch (e) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const loadBilling = async () => {
    try {
      const { data } = await http.get("/billing/status");
      setBilling(data);
    } catch { /* admin / network — fall back to backend 402 enforcement */ }
  };

  useEffect(() => { load(); loadBilling(); }, []);

  // Open the "New Project" flow automatically when the landing page sends us here.
  // If the user has no project slots, surface the upgrade modal directly instead
  // so they're not forced through a dialog they can't submit.
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      const canCreate = isSpaceCostTab
        ? billing?.can_create_spacecost_project
        : billing?.can_create_project;
      if (billing && !canCreate) {
        setUpgradeInfo({
          plan: billing.plan,
          limit: isSpaceCostTab ? billing.spacecost_project_limit : billing.project_limit,
          product: productTab,
        });
      } else {
        setNoticeOpen(true);
      }
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, billing, isSpaceCostTab, productTab]);

  const handleNewProjectClick = () => {
    const canCreate = isSpaceCostTab
      ? billing?.can_create_spacecost_project
      : billing?.can_create_project;
    if (billing && !canCreate) {
      setUpgradeInfo({
        plan: billing.plan,
        limit: isSpaceCostTab ? billing.spacecost_project_limit : billing.project_limit,
        product: productTab,
      });
      return;
    }
    // Show the Pre-Project Notice first — sets expectations that built-in
    // costs are region averages and not contractor quotes. The wizard
    // opens only after the user acknowledges.
    setNoticeOpen(true);
  };

  // Called by the Pre-Project Notice once the user confirms acknowledgement.
  const openWizardAfterNotice = () => {
    // SpaceCost skips the FlipCost goal step (no flip vs live framing) and
    // jumps straight to the Region + Construction Details step. Then
    // step 3 names + addresses the project.
    if (isSpaceCostTab) setStep(2);
    else setStep(1);
    setOpen(true);
  };

  const resetDialog = () => {
    setStep(1);
    setGoal("flip");
    setCurrency("USD");
    setConstruction({ construction_type: "", roof_type: "", foundation_type: "", wall_finish: "" });
    setPropertyType("house");
    setSeedLibraryFrom("");
    setNewName("");
    setNewAddress("");
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) resetDialog();
  };

  const onCreate = async () => {
    try {
      const p = await createProject({
        name: newName || "Untitled Project",
        address: newAddress || "",
        project_goal: isSpaceCostTab ? "live" : goal,
        currency,
        product: productTab,
        ...construction,
        ...(isSpaceCostTab ? { property_type: propertyType } : {}),
        ...(seedLibraryFrom ? { seed_library_from: seedLibraryFrom } : {}),
      });
      setOpen(false);
      resetDialog();
      loadBilling();  // refresh slot/limit counters
      navigate(`/projects/${p.id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = detail?.code;
      if (e?.response?.status === 402 && (code === "plan_limit_reached" || code === "spacecost_limit_reached")) {
        // User hit their plan's project cap — surface the upgrade modal.
        setOpen(false);
        setUpgradeInfo({
          plan: detail.plan,
          limit: detail.limit,
          product: detail.product || productTab,
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not create project");
      }
    }
  };

  const onDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this project?")) return;
    try {
      await deleteProject(id);
      setProjects(prev => prev.filter(p => p.id !== id));
      toast.success("Project deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  // Show only projects that match the active product tab. Legacy rows
  // without a `product` field count as FlipCost.
  const filteredProjects = projects.filter((p) => {
    const pp = (p.product || "flipcost").toLowerCase();
    return pp === productTab;
  });

  return (
    <div
      className="min-h-screen fc-app-bg text-[#27272A] fc-fade-in"
      data-testid="project-list-page"
      data-theme={isSpaceCostTab ? "spacecost" : undefined}
      data-product={productTab}
    >
      <header className="sticky top-0 z-20 fc-header-bar">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-2 py-4 px-3 sm:px-6 flex-wrap">
          <button
            data-testid="list-logo-btn"
            onClick={() => navigate("/")}
            className="flex items-baseline gap-2.5 sm:gap-3 group shrink-0 min-w-0"
            aria-label={isSpaceCostTab ? "SpaceCost by MyPlanCost — home" : "FlipCost by MyPlanCost — home"}
          >
            <span
              className="fc-heading text-2xl sm:text-[28px] font-extrabold tracking-tight text-[#1C1C1C] leading-none"
              data-testid="app-wordmark"
            >
              {isSpaceCostTab ? "SpaceCost" : "FlipCost"}
            </span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-[#A8A29E] font-medium leading-none whitespace-nowrap">
              by MyPlanCost
            </span>
          </button>
          <div className="flex items-center gap-2 shrink-0">
            <button
              data-testid="open-how-it-works-btn"
              onClick={() => navigate("/how-it-works")}
              aria-label="How it works"
              className="text-sm text-[#78716C] hover:text-[#1C1C1C] border border-black/[0.08] hover:border-black/[0.2] rounded-lg h-9 w-9 sm:w-auto sm:px-3 grid place-items-center sm:flex sm:items-center sm:gap-1.5 transition-colors"
            >
              <HelpCircle className="w-4 h-4" /> <span className="hidden sm:inline">How it works</span>
            </button>
            <Dialog open={open} onOpenChange={handleOpenChange}>
            <Button
              data-testid="new-project-btn"
              onClick={handleNewProjectClick}
              className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-9 px-3 text-sm font-medium whitespace-nowrap shrink-0"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              {isSpaceCostTab ? (
                <>
                  {/* Shorter label on mobile so the button doesn't wrap
                      under the wider "SpaceCost" wordmark + tagline. */}
                  <span className="sm:hidden">New project</span>
                  <span className="hidden sm:inline">New SpaceCost project</span>
                </>
              ) : (
                "New Project"
              )}
            </Button>
            <DialogContent
              data-theme={isSpaceCostTab ? "spacecost" : undefined}
              className="bg-white border-black/[0.08] text-[#27272A] sm:max-w-[640px] max-h-[90vh] overflow-y-auto"
            >              <DialogHeader>
                <DialogTitle className="fc-heading text-[#1C1C1C]">
                  {isSpaceCostTab
                    ? (step === 2 && "Tell us about the space") ||
                      (step === 3 && "Name your SpaceCost project")
                    : (step === 1 && "What's the goal of this project?") ||
                      (step === 2 && "Tell us about the building") ||
                      (step === 3 && "Name your project")}
                </DialogTitle>
                <DialogDescription className="text-xs text-[#A8A29E]">
                  {isSpaceCostTab
                    ? (step === 2 && "These details are stored on the project so you don't have to re-enter them on the Overview tab. You can edit them later.") ||
                      (step === 3 && "Add a name and we'll set up the project. You can add up to 3 rooms — buy a +3 room pack later if you need more.")
                    : (step === 1 && "Your goal shapes how results are framed. Calculations stay the same.") ||
                      (step === 2 && "Construction style materially changes shell and exterior costs — brick & mortar, wood frame, concrete and steel cost very differently.") ||
                      (step === 3 && "Add a name and address to start tracking your numbers.")}
                </DialogDescription>
              </DialogHeader>

              {!isSpaceCostTab && step === 1 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 py-2" data-testid="goal-selection">
                  {PROJECT_GOALS.map((g) => {
                    const Icon = GOAL_ICON[g.id];
                    const active = goal === g.id;
                    return (
                      <button
                        key={g.id}
                        data-testid={`goal-card-${g.id}`}
                        onClick={() => setGoal(g.id)}
                        className={`relative text-left rounded-xl border p-4 transition-all ${
                          active
                            ? "fc-sage-highlight"
                            : "border-black/[0.08] hover:bg-sage-50 hover:border-sage-200 bg-white"
                        }`}
                      >
                        {active && (
                          <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#1C1C1C] text-white grid place-items-center">
                            <Check className="w-3 h-3" />
                          </div>
                        )}
                        <div className={`w-9 h-9 rounded-lg grid place-items-center ${active ? "bg-[#1C1C1C] text-white" : "bg-sage-100 text-sage-700 border border-sage-200"}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="fc-heading text-[#1C1C1C] text-sm font-medium mt-3">{g.label}</div>
                        <div className="text-xs text-[#57534E] mt-1.5 leading-relaxed">{g.tagline}</div>
                      </button>
                    );
                  })}
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4 py-2 max-h-[55vh] overflow-y-auto pr-1" data-testid="construction-selection">
                  {!isSpaceCostTab && (
                    <div className="flex items-center gap-2 text-xs text-[#78716C]">
                      <span className="fc-label">Goal</span>
                      <span className="px-2 py-0.5 rounded-full bg-black/[0.06] text-[#1C1C1C] text-[11px]">{getGoal(goal).label}</span>
                      <button onClick={() => setStep(1)} className="underline text-[#78716C] hover:text-[#1C1C1C] text-[11px]" data-testid="back-to-goal-btn">change</button>
                    </div>
                  )}

                  <div data-testid="region-currency-picker">
                    <div className="fc-label mb-1.5 flex items-center gap-1.5">
                      <Globe className="w-3.5 h-3.5 text-[#78716C]" />
                      Region &amp; currency
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {CURRENCIES.map((c) => {
                        const active = currency === c.code;
                        return (
                          <button
                            key={c.code}
                            type="button"
                            data-testid={`region-currency-${c.code}`}
                            onClick={() => setCurrency(c.code)}
                            className={`text-left rounded-lg border p-2.5 transition-all ${
                              active
                                ? "border-sage-300 bg-sage-50 ring-2 ring-sage-200"
                                : "border-black/[0.08] hover:bg-sage-50/50 hover:border-sage-200 bg-white"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="fc-heading text-[#1C1C1C] text-[13px] font-medium truncate">
                                {REGION_BY_CURRENCY[c.code] || c.label}
                              </div>
                              {active && (
                                <div className="w-4 h-4 rounded-full bg-[#1C1C1C] text-white grid place-items-center shrink-0">
                                  <Check className="w-2.5 h-2.5" />
                                </div>
                              )}
                            </div>
                            <div className="text-[11px] text-[#78716C] mt-0.5">
                              {c.code} · {c.symbol}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <div className="text-[11px] text-[#A8A29E] mt-2">
                      Sets the project currency and scales built-in rates to this market. You can change it later from the dashboard.
                    </div>
                  </div>

                  {isSpaceCostTab && (
                    <div data-testid="space-type-picker">
                      <div className="fc-label mb-1.5">Type</div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {SPACE_TYPES.map((t) => {
                          const active = propertyType === t.value;
                          return (
                            <button
                              key={t.value}
                              type="button"
                              data-testid={`space-type-${t.value}`}
                              onClick={() => setPropertyType(t.value)}
                              className={`text-left rounded-lg border px-3 py-2 text-[13px] transition-all ${
                                active
                                  ? "border-sage-300 bg-sage-50 ring-2 ring-sage-200 text-[#1C1C1C]"
                                  : "border-black/[0.08] hover:bg-sage-50/50 hover:border-sage-200 bg-white text-[#57534E]"
                              }`}
                            >
                              {t.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <ConstructionPicker
                    label="Construction type"
                    options={CONSTRUCTION_TYPES}
                    value={construction.construction_type}
                    onChange={(v) => setConstruction((c) => ({ ...c, construction_type: v }))}
                    tidPrefix="construction-type"
                  />
                  <ConstructionPicker
                    label="Roof type"
                    options={ROOF_TYPES}
                    value={construction.roof_type}
                    onChange={(v) => setConstruction((c) => ({ ...c, roof_type: v }))}
                    tidPrefix="roof-type"
                  />
                  <ConstructionPicker
                    label="Foundation"
                    options={FOUNDATION_TYPES}
                    value={construction.foundation_type}
                    onChange={(v) => setConstruction((c) => ({ ...c, foundation_type: v }))}
                    tidPrefix="foundation-type"
                  />
                  <ConstructionPicker
                    label="Wall finish"
                    options={WALL_FINISHES}
                    value={construction.wall_finish}
                    onChange={(v) => setConstruction((c) => ({ ...c, wall_finish: v }))}
                    tidPrefix="wall-finish"
                  />
                </div>
              )}

              {step === 3 && (
                <div className="space-y-3 py-2">
                  {!isSpaceCostTab && (
                  <div className="flex items-center gap-2 text-xs text-[#78716C] flex-wrap">
                    <span className="fc-label">Goal</span>
                    <span className="px-2 py-0.5 rounded-full bg-black/[0.06] text-[#1C1C1C] text-[11px]">{getGoal(goal).label}</span>
                    <span className="text-[#D6D3D1]">·</span>
                    <span className="fc-label">Region</span>
                    <span className="px-2 py-0.5 rounded-full bg-sage-100 border border-sage-200 text-sage-700 text-[11px]">
                      {REGION_BY_CURRENCY[currency] || currency} · {currency}
                    </span>
                    <span className="text-[#D6D3D1]">·</span>
                    <span className="fc-label">Build</span>
                    <span className="px-2 py-0.5 rounded-full bg-sage-100 border border-sage-200 text-sage-700 text-[11px]">
                      {CONSTRUCTION_TYPES.find((c) => c.value === construction.construction_type)?.label}
                    </span>
                    <button onClick={() => setStep(2)} className="underline text-[#78716C] hover:text-[#1C1C1C] text-[11px]" data-testid="back-to-construction-btn">change</button>
                  </div>
                  )}
                  {isSpaceCostTab && (
                  <div className="flex items-center gap-2 text-xs text-[#78716C] flex-wrap" data-testid="spacecost-recap-row">
                    <span className="fc-label">Region</span>
                    <span className="px-2 py-0.5 rounded-full bg-sage-100 border border-sage-200 text-sage-700 text-[11px]">
                      {REGION_BY_CURRENCY[currency] || currency} · {currency}
                    </span>
                    <span className="text-[#D6D3D1]">·</span>
                    <span className="fc-label">Type</span>
                    <span className="px-2 py-0.5 rounded-full bg-sage-100 border border-sage-200 text-sage-700 text-[11px]">
                      {SPACE_TYPES.find((t) => t.value === propertyType)?.label}
                    </span>
                    <span className="text-[#D6D3D1]">·</span>
                    <span className="fc-label">Build</span>
                    <span className="px-2 py-0.5 rounded-full bg-sage-100 border border-sage-200 text-sage-700 text-[11px]">
                      {CONSTRUCTION_TYPES.find((c) => c.value === construction.construction_type)?.label}
                    </span>
                    <button onClick={() => setStep(2)} className="underline text-[#78716C] hover:text-[#1C1C1C] text-[11px]" data-testid="back-to-construction-btn">change</button>
                  </div>
                  )}
                  <div>
                    <label className="fc-label">{isSpaceCostTab ? "Project name" : "Property name"}</label>
                    <Input
                      data-testid="new-project-name-input"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder={isSpaceCostTab ? "e.g., Kitchen refit · Elmwood" : "e.g., Elmwood Ave Flip"}
                      className="fc-input mt-1.5 h-10"
                    />
                  </div>
                  <div>
                    <label className="fc-label">{isSpaceCostTab ? "Location (optional)" : "Address"}</label>
                    <Input
                      data-testid="new-project-address-input"
                      value={newAddress}
                      onChange={(e) => setNewAddress(e.target.value)}
                      placeholder={isSpaceCostTab ? "Auckland, NZ" : "123 Elmwood Ave, Auckland"}
                      className="fc-input mt-1.5 h-10"
                    />
                  </div>
                  {projects.filter((p) => !p.shared_with_me).length > 0 && (
                    <div data-testid="seed-library-picker">
                      <label className="fc-label flex items-center gap-1.5">
                        <Library className="w-3.5 h-3.5 text-[#78716C]" />
                        Copy Cost Library from… <span className="text-[#A8A29E] normal-case tracking-normal text-[11px]">(optional)</span>
                      </label>
                      <select
                        data-testid="seed-library-select"
                        value={seedLibraryFrom}
                        onChange={(e) => setSeedLibraryFrom(e.target.value)}
                        className="fc-input mt-1.5 h-10 w-full rounded-md text-sm px-2"
                      >
                        <option value="">Start with platform defaults</option>
                        {projects
                          .filter((p) => !p.shared_with_me)
                          .map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}{p.address ? ` — ${p.address}` : ""} ({p.currency || "USD"})
                            </option>
                          ))}
                      </select>
                      <div className="text-[11px] text-[#A8A29E] mt-1">
                        Reuse rates from another of your projects in the same area instead of starting blank.
                      </div>
                    </div>
                  )}
                </div>
              )}

              <DialogFooter>
                {!isSpaceCostTab && step === 1 && (
                  <Button
                    data-testid="goal-continue-btn"
                    onClick={() => setStep(2)}
                    className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E]"
                  >
                    Continue <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                )}
                {step === 2 && (
                  <Button
                    data-testid="construction-continue-btn"
                    onClick={() => setStep(3)}
                    disabled={!isConstructionComplete(construction)}
                    className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                )}
                {step === 3 && (
                  <Button
                    data-testid="confirm-create-project-btn"
                    onClick={onCreate}
                    className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E]"
                  >
                    {isSpaceCostTab ? "Create SpaceCost project" : "Create Project"}
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <UserMenu />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-3 sm:px-6 py-6 sm:py-10">
        {/* Product switcher — FlipCost (whole-property) vs SpaceCost (room-level). */}
        <div className="mb-6 flex items-center gap-2 flex-wrap" data-testid="product-tabs">
          <button
            type="button"
            data-testid="product-tab-flipcost"
            onClick={() => setProductTab("flipcost")}
            className={`inline-flex items-center gap-2 h-9 px-4 rounded-full border text-[13px] transition-colors ${
              !isSpaceCostTab
                ? "bg-[#1C1C1C] text-white border-[#1C1C1C]"
                : "bg-white text-[#57534E] border-black/[0.1] hover:border-[#A8BA99] hover:bg-[#F4F6EE]"
            }`}
          >
            <Building2 className="w-3.5 h-3.5" /> FlipCost projects
            {billing && (
              <span
                className={`text-[11px] ${
                  !isSpaceCostTab ? "opacity-70" : "text-[#4A5C3E]"
                }`}
              >
                · {billing.project_count ?? 0}
              </span>
            )}
          </button>
          <button
            type="button"
            data-testid="product-tab-spacecost"
            onClick={() => setProductTab("spacecost")}
            className={`inline-flex items-center gap-2 h-9 px-4 rounded-full border text-[13px] transition-colors ${
              isSpaceCostTab
                ? "bg-[#1C1C1C] text-white border-[#1C1C1C]"
                : "bg-white text-[#57534E] border-black/[0.1] hover:border-[#A8B470] hover:bg-[#F4F5E8]"
            }`}
          >
            <Ruler className="w-3.5 h-3.5" /> SpaceCost projects
            {billing && (
              <span
                className={`text-[11px] ${
                  isSpaceCostTab ? "opacity-70" : "text-[#6B7C3F]"
                }`}
              >
                · {billing.spacecost_project_count ?? 0}
              </span>
            )}
          </button>
        </div>

        <div className="mb-8 flex items-end justify-between">
          <div>
            <h1 className="fc-heading text-2xl font-medium tracking-tight text-[#1C1C1C]" data-testid="projects-heading">
              {isSpaceCostTab ? "SpaceCost projects" : "FlipCost projects"}
            </h1>
            <p className="text-sm text-[#78716C] mt-1">
              {isSpaceCostTab
                ? "Room-level budgets — kitchen refits, bathroom remodels, gardens, single extensions."
                : "Plan renovation costs, compare options, and decide with confidence."}
            </p>
          </div>
          <div className="text-xs text-[#A8A29E]">
            {filteredProjects.length} {filteredProjects.length === 1 ? "project" : "projects"}
          </div>
        </div>

        {loading ? (
          <div className="text-[#A8A29E] text-sm">Loading...</div>
        ) : filteredProjects.length === 0 ? (
          <div className="fc-card fc-sage p-10 text-center">
            <div className="w-12 h-12 rounded-full bg-sage-100 border border-sage-200 grid place-items-center mx-auto">
              {isSpaceCostTab ? <Ruler className="w-5 h-5 text-sage-700" /> : <Building2 className="w-5 h-5 text-sage-700" />}
            </div>
            <h2 className="fc-heading text-lg text-[#1C1C1C] mt-4">
              {isSpaceCostTab ? "No SpaceCost projects yet" : "No projects yet"}
            </h2>
            <p className="text-sm text-[#57534E] mt-1">
              {isSpaceCostTab
                ? "Start your first room budget — kitchen, bathroom, garden, or any single space."
                : "Start your first renovation plan in under a minute."}
            </p>
            <Button
              data-testid="empty-state-new-project-btn"
              onClick={handleNewProjectClick}
              className="mt-5 bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg"
            >
              <Plus className="w-4 h-4 mr-1.5" /> {isSpaceCostTab ? "New SpaceCost project" : "New Project"}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
            {filteredProjects.map((p) => {
              const reno = estimatedRenovationCost(p);
              const roi = estimatedROI(p);
              return (
                <div
                  key={p.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/projects/${p.id}`)}
                  onKeyDown={(e) => e.key === "Enter" && navigate(`/projects/${p.id}`)}
                  className="fc-card p-5 cursor-pointer group"
                  data-testid={`project-card-${p.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <div className="fc-heading text-[#1C1C1C] text-[15px] font-medium truncate">{p.name}</div>
                      <div className="text-xs text-[#A8A29E] mt-0.5 truncate">{p.address || "No address"}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-medium text-[#A8A29E] uppercase tracking-[0.15em]">
                        {p.currency || "USD"}
                      </span>
                      <StatusPill status={p.status} />
                    </div>
                  </div>

                  <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                    <span
                      data-testid={`project-goal-badge-${p.id}`}
                      className="inline-flex items-center gap-1.5 text-[11px] fc-goal-pill rounded-full px-2 py-0.5"
                    >
                      {(() => { const Icon = GOAL_ICON[p.project_goal] || GOAL_ICON.flip; return <Icon className="w-3 h-3" />; })()}
                      {getGoal(p.project_goal).label}
                    </span>
                    {p.shared_with_me && (
                      <span
                        data-testid={`project-shared-badge-${p.id}`}
                        title={p.permission === "edit" ? "Shared with you · Can edit" : "Shared with you · View only"}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full bg-sage-100 text-sage-700 border border-sage-200"
                      >
                        <Users className="w-3 h-3" />
                        Shared
                        {p.permission === "edit" ? (
                          <Pencil className="w-2.5 h-2.5 ml-0.5" />
                        ) : (
                          <Eye className="w-2.5 h-2.5 ml-0.5" />
                        )}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-5">
                    <Metric label="Est. Reno Cost" value={formatCurrency(reno, p.currency)} />
                    <Metric label="ROI" value={formatPct(roi)} valueClass={roi >= 0 ? "text-[#059669]" : "text-[#DC2626]"} />
                  </div>

                  <div className="flex items-center justify-between mt-5 pt-4 border-t border-black/[0.06]">
                    {p.shared_with_me ? (
                      <span className="text-[11px] text-[#A8A29E] italic">Owned by someone else</span>
                    ) : (
                      <button
                        data-testid={`delete-project-${p.id}`}
                        onClick={(e) => onDelete(e, p.id)}
                        className="text-[#A8A29E] hover:text-[#DC2626] transition-colors"
                        aria-label="Delete project"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    <div className="text-xs text-[#78716C] flex items-center gap-1 group-hover:text-[#1C1C1C] transition-colors">
                      Open <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      <UpgradeModal
        open={!!upgradeInfo}
        onOpenChange={(v) => !v && setUpgradeInfo(null)}
        currentPlan={upgradeInfo?.plan}
        projectLimit={upgradeInfo?.limit}
        product={upgradeInfo?.product || "flipcost"}
      />
      <PreProjectNoticeDialog
        open={noticeOpen}
        onOpenChange={setNoticeOpen}
        onContinue={openWizardAfterNotice}
        product={productTab}
      />
    </div>
  );
}

function Metric({ label, value, valueClass = "text-[#1C1C1C]" }) {
  return (
    <div>
      <div className="fc-label">{label}</div>
      <div className={`fc-heading text-lg mt-1 ${valueClass}`}>{value}</div>
    </div>
  );
}

function StatusPill({ status = "draft" }) {
  const map = {
    draft: { label: "Draft", cls: "bg-sage-50 text-sage-700 border-sage-200" },
    active: { label: "Active", cls: "bg-emerald-500/10 text-[#059669] border-emerald-500/20" },
    completed: { label: "Completed", cls: "bg-black/[0.08] text-[#1C1C1C] border-black/[0.15]" },
  };
  const s = map[status] || map.draft;
  return (
    <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full border ${s.cls}`}>
      {s.label}
    </span>
  );
}

// Compact two-line picker — used four times in step 2 of the New Project dialog.
function ConstructionPicker({ label, options, value, onChange, tidPrefix }) {
  return (
    <div data-testid={`${tidPrefix}-picker`}>
      <div className="fc-label mb-1.5">{label}</div>
      <div className="grid grid-cols-2 gap-2">
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              data-testid={`${tidPrefix}-${opt.value}`}
              onClick={() => onChange(opt.value)}
              className={`text-left rounded-lg border p-3 transition-all ${
                active
                  ? "border-sage-300 bg-sage-50 ring-2 ring-sage-200"
                  : "border-black/[0.08] hover:bg-sage-50/50 hover:border-sage-200 bg-white"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="fc-heading text-[#1C1C1C] text-sm font-medium">{opt.label}</div>
                {active && (
                  <div className="w-4 h-4 rounded-full bg-[#1C1C1C] text-white grid place-items-center shrink-0">
                    <Check className="w-2.5 h-2.5" />
                  </div>
                )}
              </div>
              <div className="text-[11px] text-[#78716C] mt-1 leading-relaxed">{opt.sub}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
